import { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import { supabase } from "../../supabaseClient"
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card"
import { Button } from "../../components/ui/button"
import { SchoolSearchInput } from "../../components/SchoolSearchInput"
import { getActiveYear } from "../../utils/activeYear"
import { generateAllGames } from "../../utils/bracket"

interface School {
  id: number
  name: string
}

interface SavedTeam {
  id: number
  name: string
  seed: number
  region: string
  region_position: string
}

const REGION_POSITIONS = [
  { position: 'top-left', label: 'Top-Left' },
  { position: 'top-right', label: 'Top-Right' },
  { position: 'bottom-left', label: 'Bottom-Left' },
  { position: 'bottom-right', label: 'Bottom-Right' },
]

const REGIONS = ['East', 'West', 'South', 'Midwest']

// Bracket matchup order - how seeds appear in a real bracket
const BRACKET_MATCHUPS: [number, number][] = [
  [1, 16],
  [8, 9],
  [5, 12],
  [4, 13],
  [6, 11],
  [3, 14],
  [7, 10],
  [2, 15],
]

export default function YearSetup() {
  const [schools, setSchools] = useState<School[]>([])
  const [activeYear, setActiveYearState] = useState<number | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  
  // Is the bracket already locked (64 teams saved)?
  const [isLocked, setIsLocked] = useState(false)
  const [savedTeams, setSavedTeams] = useState<SavedTeam[]>([])
  
  // Region position assignments - start with empty (no defaults)
  const [regionPositions, setRegionPositions] = useState<Record<string, string>>({
    'top-left': '',
    'top-right': '',
    'bottom-left': '',
    'bottom-right': '',
  })

  // Team assignments: position -> seed -> school name
  const [teamAssignments, setTeamAssignments] = useState<Record<string, Record<number, string>>>({
    'top-left': {},
    'top-right': {},
    'bottom-left': {},
    'bottom-right': {},
  })

  const [activePosition, setActivePosition] = useState('top-left')

  useEffect(() => {
    loadActiveYear()
  }, [])

  async function loadActiveYear() {
    const year = await getActiveYear()
    setActiveYearState(year)
    loadData(year)
  }

  async function loadData(year: number) {
    setLoading(true)

    // Load schools for autocomplete
    const { data: schoolsData } = await supabase
      .from('team_schools')
      .select('id, name')
      .order('name')

    // Load existing tournament teams for active year (joined with school names)
    const { data: teamsData } = await supabase
      .from('tournament_teams')
      .select('id, seed, region, region_position, school:team_schools(name)')
      .eq('year', year)

    setSchools(schoolsData || [])

    // Check if bracket is locked (64 teams already saved)
    if (teamsData && teamsData.length === 64) {
      setIsLocked(true)
      // Transform saved teams for display
      const teams = teamsData.map((t: any) => ({
        id: t.id,
        name: t.school?.name || 'Unknown',
        seed: t.seed,
        region: t.region,
        region_position: t.region_position,
      }))
      setSavedTeams(teams)
    } else {
      setIsLocked(false)
      setSavedTeams([])
      
      // Populate team assignments from existing data (partial setup)
      if (teamsData && teamsData.length > 0) {
        const newAssignments: Record<string, Record<number, string>> = {
          'top-left': {},
          'top-right': {},
          'bottom-left': {},
          'bottom-right': {},
        }
        const newPositions: Record<string, string> = {
          'top-left': '',
          'top-right': '',
          'bottom-left': '',
          'bottom-right': '',
        }

        teamsData.forEach((team: any) => {
          if (team.region_position && team.school?.name) {
            newAssignments[team.region_position][team.seed] = team.school.name
            newPositions[team.region_position] = team.region
          }
        })

        setRegionPositions(newPositions)
        setTeamAssignments(newAssignments)
      } else {
        // Reset if no teams
        setTeamAssignments({
          'top-left': {},
          'top-right': {},
          'bottom-left': {},
          'bottom-right': {},
        })
        setRegionPositions({
          'top-left': '',
          'top-right': '',
          'bottom-left': '',
          'bottom-right': '',
        })
      }
    }

    setLoading(false)
  }

  function updateTeam(position: string, seed: number, schoolName: string) {
    setTeamAssignments(prev => ({
      ...prev,
      [position]: {
        ...prev[position],
        [seed]: schoolName
      }
    }))
  }

  function updateRegion(position: string, region: string) {
    setRegionPositions(prev => ({ ...prev, [position]: region }))
  }

  // Get regions already used in other positions
  function getUsedRegions(currentPosition: string): string[] {
    return Object.entries(regionPositions)
      .filter(([pos, region]) => pos !== currentPosition && region)
      .map(([, region]) => region)
  }

  // Get available regions for a position
  function getAvailableRegions(currentPosition: string): string[] {
    const used = getUsedRegions(currentPosition)
    return REGIONS.filter(r => !used.includes(r))
  }

  async function saveTeams() {
    if (!activeYear) return
    
    // Validate all regions are assigned
    const unassignedPositions = REGION_POSITIONS.filter(
      ({ position }) => !regionPositions[position]
    )
    if (unassignedPositions.length > 0) {
      alert(`Please assign regions to all bracket positions: ${unassignedPositions.map(p => p.label).join(', ')}`)
      return
    }

    setSaving(true)

    // Delete existing tournament teams for this year
    await supabase.from('tournament_teams').delete().eq('year', activeYear)

    // Build new team entries with school_id
    const newTeams: any[] = []

    for (const position of Object.keys(teamAssignments)) {
      const region = regionPositions[position]
      const positionTeams = teamAssignments[position]

      for (const seedStr of Object.keys(positionTeams)) {
        const seed = parseInt(seedStr)
        const teamName = positionTeams[seed]?.trim()
        
        if (teamName) {
          // Find school_id from schools list
          const school = schools.find(s => s.name.toLowerCase() === teamName.toLowerCase())
          if (school) {
            newTeams.push({
              school_id: school.id,
              year: activeYear,
              seed,
              region,
              region_position: position,
              wins: 0,
              is_eliminated: false
            })
          } else {
            console.warn(`School not found: ${teamName}`)
          }
        }
      }
    }

    if (newTeams.length === 0) {
      alert('No teams to save! Make sure team names match schools in the database.')
      setSaving(false)
      return
    }

    // Must have exactly 64 teams to save
    if (newTeams.length !== 64) {
      alert(`You must enter all 64 teams before saving. Currently have ${newTeams.length}.`)
      setSaving(false)
      return
    }

    // Insert tournament teams
    const { data: insertedTeams, error } = await supabase
      .from('tournament_teams')
      .insert(newTeams)
      .select('*, school:team_schools(name)')

    if (error) {
      console.error('Error saving teams:', error)
      alert('Error saving teams: ' + error.message)
      setSaving(false)
      return
    }

    // Generate all 63 games
    if (insertedTeams) {
      const teamsWithIds = insertedTeams.map((t: any) => ({
        id: t.id,
        name: t.school?.name || '',
        seed: t.seed,
        region: t.region,
        region_position: t.region_position,
      }))

      const gamesCreated = await generateAllGames(activeYear, teamsWithIds)
      
      if (gamesCreated) {
        alert(`✓ Saved 64 teams\n✓ Generated 63 bracket games\n\nBracket is now locked. Go to Bracket Manager to enter results!`)
      } else {
        alert(`✓ Saved 64 teams\n✗ Error generating games - check console`)
      }
    }

    setSaving(false)
    loadData(activeYear)
  }

  // Count filled teams
  const filledCount = Object.values(teamAssignments).reduce((total, pos) => 
    total + Object.values(pos).filter(name => name?.trim()).length, 0
  )

  if (loading) {
    return <div className="flex items-center justify-center min-h-[60vh] text-gray-500">Loading...</div>
  }

  // Regenerate games for locked bracket
  async function regenerateGames() {
    if (!activeYear) return
    setSaving(true)
    
    // Reset team stats (wins and is_eliminated) for this year
    await supabase
      .from('tournament_teams')
      .update({ wins: 0, is_eliminated: false })
      .eq('year', activeYear)
    
    // Convert saved teams to the format needed for game generation
    const teamsForGeneration = savedTeams.map(t => ({
      id: t.id,
      name: t.name,
      seed: t.seed,
      region: t.region,
      region_position: t.region_position,
    }))

    const gamesCreated = await generateAllGames(activeYear, teamsForGeneration)
    
    if (gamesCreated) {
      alert('✓ Generated 63 bracket games!\n\nGo to Bracket Manager to enter results.')
    } else {
      alert('Error generating games - check console for details')
    }
    
    setSaving(false)
  }

  // LOCKED VIEW - Show read-only teams
  if (isLocked) {
    // Group saved teams by region position
    const teamsByPosition: Record<string, SavedTeam[]> = {}
    savedTeams.forEach(team => {
      if (!teamsByPosition[team.region_position]) {
        teamsByPosition[team.region_position] = []
      }
      teamsByPosition[team.region_position].push(team)
    })

    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Link to="/admin" className="text-gray-400 hover:text-gray-600">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M19 12H5M12 19l-7-7 7-7"/>
            </svg>
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-[#1e3a5f]">Teams Setup</h1>
            <p className="text-gray-500">{activeYear} Tournament</p>
          </div>
        </div>

        {/* Locked Banner - Simple */}
        <Card className="bg-white border-0 shadow-sm">
          <CardContent className="py-4">
            <div className="flex items-center gap-2 text-[#1e3a5f] mb-3">
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
              </svg>
              <span className="font-semibold">Bracket Locked</span>
              <span className="text-sm text-gray-500 font-normal">— 64 teams are set. Use Bracket Manager to enter game results.</span>
            </div>
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={regenerateGames}
                disabled={saving}
                className="px-3 py-1.5 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-colors text-sm font-medium disabled:opacity-50"
              >
                {saving ? 'Generating...' : 'Regenerate Bracket'}
              </button>
              <Link 
                to="/admin/bracket" 
                className="px-3 py-1.5 bg-[#1e3a5f] text-white rounded-lg hover:bg-[#002a6b] transition-colors text-sm font-medium"
              >
                Go to Bracket Manager →
              </Link>
            </div>
          </CardContent>
        </Card>

        {/* Read-only Teams Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {REGION_POSITIONS.map(({ position, label }) => {
            const positionTeams = teamsByPosition[position] || []
            const region = positionTeams[0]?.region || ''
            
            return (
              <Card key={position} className="bg-white border-0 shadow-sm">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base text-[#1e3a5f]">
                    {label} • {region}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-1">
                    {BRACKET_MATCHUPS.map(([seed1, seed2], idx) => {
                      const team1 = positionTeams.find(t => t.seed === seed1)
                      const team2 = positionTeams.find(t => t.seed === seed2)
                      return (
                        <div key={idx} className="flex items-center gap-2 text-sm py-1 px-2 bg-gray-50 rounded">
                          <span className="w-5 text-center font-bold text-[#1e3a5f]">{seed1}</span>
                          <span className="flex-1 truncate">{team1?.name || '-'}</span>
                          <span className="text-gray-400 text-xs">vs</span>
                          <span className="w-5 text-center font-bold text-[#1e3a5f]">{seed2}</span>
                          <span className="flex-1 truncate">{team2?.name || '-'}</span>
                        </div>
                      )
                    })}
                  </div>
                </CardContent>
              </Card>
            )
          })}
        </div>
      </div>
    )
  }

  // EDITABLE VIEW - Team entry form
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link to="/admin" className="text-gray-400 hover:text-gray-600">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 12H5M12 19l-7-7 7-7"/>
          </svg>
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-[#1e3a5f]">Teams Setup</h1>
          <p className="text-gray-500">Assign teams for <span className="font-semibold text-[#F15A22]">{activeYear}</span> tournament</p>
        </div>
      </div>

      {/* Warning */}
      <Card className="bg-amber-50 border-amber-200">
        <CardContent className="py-3">
          <div className="flex items-center gap-2 text-amber-800 text-sm">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
              <line x1="12" y1="9" x2="12" y2="13"/>
              <line x1="12" y1="17" x2="12.01" y2="17"/>
            </svg>
            <span><strong>One-time setup:</strong> Once you save all 64 teams, the bracket will be locked and cannot be edited.</span>
          </div>
        </CardContent>
      </Card>

      {/* Region Position Selector */}
      <Card className="bg-white border-0 shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg text-[#1e3a5f]">Bracket Positions</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-gray-500 mb-4">Assign regions to bracket positions (Final Four: Top-Left vs Bottom-Left, Top-Right vs Bottom-Right)</p>
          <div className="grid grid-cols-2 gap-4">
            {REGION_POSITIONS.map(({ position, label }) => {
              const availableRegions = getAvailableRegions(position)
              const currentRegion = regionPositions[position]
              
              return (
                <div key={position} className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">{label}</label>
                  <select
                    value={currentRegion}
                    onChange={(e) => updateRegion(position, e.target.value)}
                    className={`w-full px-3 py-2 border rounded-lg ${
                      currentRegion ? 'border-green-300 bg-green-50' : 'border-gray-200'
                    }`}
                  >
                    <option value="">Select region...</option>
                    {/* Show current selection even if used elsewhere */}
                    {currentRegion && <option value={currentRegion}>{currentRegion}</option>}
                    {/* Show available regions */}
                    {availableRegions
                      .filter(r => r !== currentRegion)
                      .map(r => (
                        <option key={r} value={r}>{r}</option>
                      ))
                    }
                  </select>
                </div>
              )
            })}
          </div>
        </CardContent>
      </Card>

      {/* Team Entry */}
      <Card className="bg-white border-0 shadow-sm">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <CardTitle className="text-lg text-[#1e3a5f]">
              {activeYear} Teams ({filledCount}/64)
            </CardTitle>
            <Button 
              onClick={saveTeams} 
              disabled={saving || filledCount !== 64} 
              className={`${filledCount === 64 ? 'bg-green-600 hover:bg-green-700' : 'bg-gray-400 cursor-not-allowed'}`}
            >
              {saving ? 'Saving...' : 'Save & Lock Bracket'}
            </Button>
          </div>
          {filledCount === 64 ? (
            <p className="text-sm text-green-600 mt-1">✓ All 64 teams entered - ready to lock bracket</p>
          ) : (
            <p className="text-sm text-gray-500 mt-1">Enter all 64 teams to save and lock the bracket</p>
          )}
        </CardHeader>
        <CardContent>
          {/* Position Tabs */}
          <div className="flex flex-wrap gap-2 mb-4">
            {REGION_POSITIONS.map(({ position, label }) => {
              const filledInPosition = Object.values(teamAssignments[position]).filter(n => n?.trim()).length
              const isComplete = filledInPosition === 16
              const hasRegion = !!regionPositions[position]
              return (
                <button
                  key={position}
                  onClick={() => setActivePosition(position)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    activePosition === position
                      ? 'bg-[#1e3a5f] text-white'
                      : isComplete
                        ? 'bg-green-100 text-green-700 hover:bg-green-200'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {label} {hasRegion ? `(${regionPositions[position]})` : ''}
                  <span className="ml-2 opacity-70">{filledInPosition}/16</span>
                  {isComplete && activePosition !== position && ' ✓'}
                </button>
              )
            })}
          </div>

          {/* Seed Inputs for Active Position - Bracket Matchup Layout */}
          <div className="border border-gray-200 rounded-lg p-4">
            <div className="text-sm font-medium text-gray-500 mb-3">
              {regionPositions[activePosition] 
                ? `${regionPositions[activePosition]} Region • ${REGION_POSITIONS.find(p => p.position === activePosition)?.label}`
                : `${REGION_POSITIONS.find(p => p.position === activePosition)?.label} • Select a region above`
              }
            </div>
            
            {/* Bracket matchup pairs */}
            <div className="space-y-3">
              {BRACKET_MATCHUPS.map(([seed1, seed2], matchupIndex) => (
                <div key={matchupIndex} className="flex items-stretch gap-2 p-2 bg-gray-50 rounded-lg">
                  {/* First seed in matchup */}
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="w-7 h-7 bg-[#1e3a5f] text-white rounded flex items-center justify-center text-xs font-bold shrink-0">
                        {seed1}
                      </span>
                      <SchoolSearchInput
                        schools={schools}
                        value={teamAssignments[activePosition][seed1] || ''}
                        onChange={(name) => updateTeam(activePosition, seed1, name)}
                        isSelected={!!teamAssignments[activePosition][seed1]?.trim()}
                      />
                    </div>
                  </div>

                  {/* VS divider */}
                  <div className="flex items-center px-2 text-gray-400 text-xs font-medium">
                    vs
                  </div>

                  {/* Second seed in matchup */}
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="w-7 h-7 bg-[#1e3a5f] text-white rounded flex items-center justify-center text-xs font-bold shrink-0">
                        {seed2}
                      </span>
                      <SchoolSearchInput
                        schools={schools}
                        value={teamAssignments[activePosition][seed2] || ''}
                        onChange={(name) => updateTeam(activePosition, seed2, name)}
                        isSelected={!!teamAssignments[activePosition][seed2]?.trim()}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
